import { useEffect, useState } from 'react';

export interface DebugInfo {
  readonly apiBaseUrl: string;
  readonly apiKey: string;
  readonly web3AuthClientId: string;
  readonly forceDevWallet: boolean;
  readonly chainId: string;
  readonly tokenSymbol: string;
  readonly fiatCurrency: string;
  readonly isProduction: boolean;
}

/**
 * Visible runtime diagnostic for stuck-in-prod debugging from a phone
 * (no devtools). Renders only when the page URL has `?debug=1` so it
 * never leaks to real users. Echoes the Vite-baked environment as the
 * running bundle sees it — the single source of truth when a previous
 * build cached a stale value or an env var didn't reach the right
 * Vercel scope.
 *
 * Secret-ish values (`apiKey`, `web3AuthClientId`) are partially
 * masked: enough to tell empty from set, not enough to use elsewhere.
 */
export function DebugBanner({ info }: { info: DebugInfo }): JSX.Element | null {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      setEnabled(params.get('debug') === '1');
    } catch {
      // SSR or sandboxed iframe — silently no-op
    }
  }, []);

  if (!enabled) return null;

  const rows: Array<readonly [string, string]> = [
    ['apiBaseUrl', presentString(info.apiBaseUrl)],
    ['apiKey', maskTail(info.apiKey)],
    ['web3AuthClientId', maskTail(info.web3AuthClientId)],
    ['forceDevWallet', String(info.forceDevWallet)],
    ['chainId', info.chainId],
    ['tokenSymbol', info.tokenSymbol],
    ['fiatCurrency', info.fiatCurrency],
    ['mode', info.isProduction ? 'production' : 'development'],
    ['origin', tryOrigin()],
  ];

  return (
    <div
      role="region"
      aria-label="Crypto-pay widget debug info"
      style={{
        margin: '8px 0 12px',
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid #475569',
        background: '#0f172a',
        color: '#e2e8f0',
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#fde68a' }}>
        🔎 Debug (URL ?debug=1)
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td
                style={{
                  padding: '1px 8px 1px 0',
                  color: '#94a3b8',
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                {k}
              </td>
              <td style={{ padding: '1px 0', wordBreak: 'break-all' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function presentString(value: string): string {
  if (!value) return '∅ (empty)';
  return JSON.stringify(value);
}

function maskTail(value: string): string {
  if (!value) return '∅ (empty)';
  if (value.length <= 8) return `"…" (${value.length} chars)`;
  return `"${value.slice(0, 6)}…${value.slice(-2)}" (${value.length} chars)`;
}

function tryOrigin(): string {
  try {
    return window.location.origin;
  } catch {
    return '∅';
  }
}
